import { Box, Stack, Typography, styled, useTheme } from "@mui/material"
import type { ReactNode } from "react"
import { ChevronRight } from "react-feather"
import { Link } from "react-router-dom"
import type { MergeExclusive } from "type-fest"
import { AmountChip, formatNumber, formatPrice } from "../../dataDisplay"
import {
  CardLine,
  CardLineAction,
  CardLineContentGrid,
  ICON_SIZE,
  ProgressBar,
  type ProgressBarProps,
  ProgressStatus,
  SmallLabel,
  getPluralizedWording,
} from "./common"

export enum GoalTrackingVariant {
  OPERATION = "operation",
  CHALLENGE = "challenge",
}

interface CommonProps {
  link?: string
  title?: string
  tag?: string
  status: {
    boldPart: ReactNode
    regularPart: string
  }
  showTargetChip?: boolean
  // Data
  currentValue: number
  goal: number
}

export interface OperationTrackingRowProps extends CommonProps {
  variant: GoalTrackingVariant.OPERATION
}
export interface ChallengeTrackingRowProps extends CommonProps {
  variant: GoalTrackingVariant.CHALLENGE
  lastYearValue: number
}
export type GoalTrackingRowProps = MergeExclusive<
  OperationTrackingRowProps,
  ChallengeTrackingRowProps
>

const GoalTrackingRow = ({
  variant,
  link,
  title,
  tag,
  status,
  showTargetChip = true,
  // Data
  currentValue,
  lastYearValue,
  goal,
}: GoalTrackingRowProps) => {
  const {
    spacing,
    palette: { common },
  } = useTheme()

  const { color, unit, size, showZero, maxGainTitle } = useVariantConfig(variant)
  const isOperation = variant === GoalTrackingVariant.OPERATION
  const progress = Math.min((100 * currentValue) / goal, 100)
  const progressBarProps: Omit<
    ProgressBarProps,
    "color" | "progress" | "goal" | "unit" | "currentValue"
  > = isOperation
    ? {
        showEndMark: false,
      }
    : {
        referenceValue: lastYearValue,
        showHalfMark: true,
        endLabelPosition: "after",
      }
  const isMaxProgress = progress === 100
  const remainingAmount =
    isMaxProgress || progress === 0 ? null : goal - currentValue

  return (
    <Stack spacing={1}>
      {/* TITLE */}
      <Stack direction="row" spacing={1}>
        {!!title && (
          <Typography ml={4} fontWeight={500}>
            {title}
          </Typography>
        )}
        {!!tag && <Typography color="grey.dark">{tag}</Typography>}
      </Stack>

      {/* ROW */}
      <Stack direction="row" spacing={2}>
        {/* PROGRESS LINE */}
        <CardLine>
          <CardLineAction
            size={size}
            {...(link
              ? { component: Link, to: link }
              : { sx: { pointerEvents: "none" } })}
          >
            <CardLineContentGrid hasThirdColumn={isOperation}>
              <ProgressStatus
                progress={progress}
                title={
                  <span style={{ whiteSpace: "nowrap" }}>
                    <b>{status.boldPart}</b>
                    &nbsp;
                    {status.regularPart}
                  </span>
                }
                color={color}
                showZero={showZero}
              />

              <ProgressBar
                color={color}
                progress={progress}
                goal={goal}
                unit={unit}
                currentValue={currentValue}
                {...progressBarProps}
              />

              {isOperation && (
                <Box
                  textAlign="center"
                  position="relative"
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <SmallLabel
                    position="absolute"
                    top={spacing(-6)}
                    textAlign="center"
                  >
                    Reste à justifier
                  </SmallLabel>
                  <Typography variant="18px" fontWeight={600}>
                    {remainingAmount === null
                      ? "-"
                      : `${formatNumber(remainingAmount)} ${getPluralizedWording(
                          unit,
                          remainingAmount,
                        )}`}
                  </Typography>
                </Box>
              )}
            </CardLineContentGrid>

            {!!link && (
              <Box position="relative" width="14px" height="16px">
                <ChevronRight
                  color={common.paleBlue}
                  size={ICON_SIZE}
                  style={{
                    // This svg contains unwanted margins conflicting with given size
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              </Box>
            )}
          </CardLineAction>
        </CardLine>

        {/* TARGET CHIP */}
        {!!showTargetChip && (
          <Box position="relative">
            <SmallLabel
              position="absolute"
              top={spacing(-3)}
              textAlign="center"
              width="100%"
            >
              {isMaxProgress ? (
                <Typography component="span" color="success.main" fontSize="inherit">
                  Validé
                </Typography>
              ) : (
                maxGainTitle
              )}
            </SmallLabel>
            <AmountChip
              color={color}
              unit={unit}
              variant={
                (isOperation && !isMaxProgress) || progress === 0
                  ? "outlined"
                  : "contained"
              }
              hasUnderline={(isOperation && !isMaxProgress) || progress === 0}
              sx={{ minWidth: "120px", height: "100%" }}
            >
              {isOperation ? goal : currentValue}
            </AmountChip>
          </Box>
        )}
      </Stack>
    </Stack>
  )
}

export const OperationTrackingRow = (
  props: Omit<OperationTrackingRowProps, "variant" | "status">,
) => (
  <GoalTrackingRow
    variant={GoalTrackingVariant.OPERATION}
    status={{
      boldPart: formatPrice(Math.min(props.currentValue, props.goal)),
      regularPart: props.currentValue >= 1 ? "justifiés" : "justifié",
    }}
    {...props}
  />
)

export const ChallengeTrackingRow = (
  props: Omit<ChallengeTrackingRowProps, "variant">,
) => <GoalTrackingRow variant={GoalTrackingVariant.CHALLENGE} {...props} />

export const GoalTrackingContainer = styled(Stack)({})
GoalTrackingContainer.defaultProps = {
  spacing: 3,
}

const useVariantConfig = (variant: GoalTrackingVariant) => {
  const { palette } = useTheme()
  switch (variant) {
    case GoalTrackingVariant.OPERATION:
      return {
        color: palette.common.electricBlue,
        unit: "€",
        size: "large" as const,
        showZero: false,
        maxGainTitle: "Prime",
      }
    case GoalTrackingVariant.CHALLENGE:
      return {
        color: palette.loyalty.main,
        unit: "pts",
        size: "large" as const,
        showZero: true,
        maxGainTitle: "Points cumulés",
      }
  }
}
